import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0
  },
  drawerPaper: {
    width: drawerWidth,
    display: "flex",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4
  },
  header: {
    display: "flex",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    backgroundColor: "#eee",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    minHeight: "73px",
    justifyContent: "flex-start"
  },
  content: {
    display: "flex",
    backgroundColor: "#eee",
    flexDirection: "column",
    padding: "8px 0px 8px 8px",
    height: "100%",
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  contactAvatar: {
    margin: 15,
    width: 160,
    height: 160
  },
  contactHeader: {
    display: "flex",
    padding: 8,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    "& > *": {
      margin: 4
    }
  },
  contactDetails: {
    marginTop: 8,
    padding: 8,
    display: "flex",
    flexDirection: "column"
  },
  infoTabs: {
    minHeight: 34,
    marginBottom: 6,
    borderBottom: "1px solid rgba(148, 163, 184, 0.28)"
  },
  infoTab: {
    minHeight: 34,
    minWidth: 0,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.76rem",
    lineHeight: 1.2
  },
  contactExtraInfo: {
    marginTop: 4,
    padding: 6
  },
  historySection: {
    marginTop: 8,
    padding: 8,
    display: "flex",
    flexDirection: "column",
    gap: 8
  },
  historyItem: {
    padding: 10,
    cursor: "pointer",
    borderRadius: 10,
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: theme.shadows[3]
    }
  },
  historyItemActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(7, 94, 84, 0.18)"
        : "rgba(7, 94, 84, 0.08)"
  },
  historyMeta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 4
  },
  historyBadges: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap"
  },
  historyStatusChip: {
    fontWeight: 700
  },
  historyMessage: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
    lineHeight: 1.35,
    wordBreak: "break-word"
  },
  operationButtons: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 8
  },
  operationResult: {
    marginTop: 8,
    padding: 8,
    borderRadius: 8,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(2, 6, 23, 0.45)"
        : "rgba(248, 250, 252, 0.95)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.24)"
        : "rgba(148, 163, 184, 0.35)"
    }`
  },
  operationResultCode: {
    fontFamily: "Consolas, 'Courier New', monospace",
    fontSize: "0.78rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: theme.palette.text.secondary
  }
}));

const formatTicketNumber = ticketId =>
  `#${String(ticketId || "").padStart(6, "0")}`;

const formatTicketStatus = status => {
  switch (status) {
    case "open":
      return "Atendendo";
    case "pending":
      return "Aguardando";
    case "closed":
      return "Resolvido";
    default:
      return status;
  }
};

const formatDocument = value => {
  const digits = (value || "").replace(/\D/g, "");

  if (!digits) return "";

  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1-$2");
  }

  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

const formatDocumentDisplay = value => formatDocument(value) || "-";

const normalizeDigits = value => String(value || "").replace(/\D/g, "");
const normalizeContact = value =>
  value && typeof value === "object" ? value : {};

const normalizePersonType = value => {
  const normalized = String(value || "").trim().toUpperCase();
  if (normalized === "PF" || normalized === "PJ") {
    return normalized;
  }
  return "";
};

const isRepeatedDigits = value => /^(\d)\1+$/.test(value);

const isValidCpf = value => {
  const cpf = normalizeDigits(value);
  if (cpf.length !== 11 || isRepeatedDigits(cpf)) {
    return false;
  }

  let sum = 0;
  for (let index = 0; index < 9; index += 1) {
    sum += Number(cpf[index]) * (10 - index);
  }

  let digit = (sum * 10) % 11;
  if (digit === 10) {
    digit = 0;
  }

  if (digit !== Number(cpf[9])) {
    return false;
  }

  sum = 0;
  for (let index = 0; index < 10; index += 1) {
    sum += Number(cpf[index]) * (11 - index);
  }

  digit = (sum * 10) % 11;
  if (digit === 10) {
    digit = 0;
  }

  return digit === Number(cpf[10]);
};

const isValidCnpj = value => {
  const cnpj = normalizeDigits(value);
  if (cnpj.length !== 14 || isRepeatedDigits(cnpj)) {
    return false;
  }

  const calcDigit = (base, factors) => {
    const total = base
      .split("")
      .reduce((sum, item, index) => sum + Number(item) * factors[index], 0);
    const remainder = total % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const digit1 = calcDigit(cnpj.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  if (digit1 !== Number(cnpj[12])) {
    return false;
  }

  const digit2 = calcDigit(cnpj.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return digit2 === Number(cnpj[13]);
};

const validateDocumentAndType = (documentValue, personTypeValue) => {
  const document = normalizeDigits(documentValue);
  const personType = normalizePersonType(personTypeValue);

  if (!document) {
    return {
      valid: false,
      message: "Informe CPF/CNPJ para executar esta acao."
    };
  }

  const inferredPersonType = document.length === 11 ? "PF" : document.length === 14 ? "PJ" : "";
  if (!inferredPersonType) {
    return {
      valid: false,
      message: "CPF/CNPJ invalido. Informe 11 (CPF) ou 14 (CNPJ) digitos."
    };
  }

  const effectivePersonType = personType || inferredPersonType;
  if (effectivePersonType === "PF" && !isValidCpf(document)) {
    return {
      valid: false,
      message: "CPF invalido. Revise os digitos informados."
    };
  }

  if (effectivePersonType === "PJ" && !isValidCnpj(document)) {
    return {
      valid: false,
      message: "CNPJ invalido. Revise os digitos informados."
    };
  }

  if (personType && personType !== inferredPersonType) {
    return {
      valid: false,
      message: "Tipo de cadastro nao confere com o documento informado."
    };
  }

  return {
    valid: true,
    document,
    personType: effectivePersonType
  };
};

const normalizeResultMode = value => {
  const normalized = String(value || "display")
    .trim()
    .toLowerCase();

  if (["display", "send_conversation", "open_redirect"].includes(normalized)) {
    return normalized;
  }

  return "display";
};

const isHttpUrl = value => {
  try {
    const parsed = new URL(String(value || ""));
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const getFirstHttpUrlFromText = value => {
  const text = String(value || "");
  const match = text.match(/https?:\/\/[^\s"']+/i);
  return match ? match[0] : "";
};

const findFirstUrlInJson = value => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return isHttpUrl(value) ? value : "";
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findFirstUrlInJson(item);
      if (found) {
        return found;
      }
    }
    return "";
  }

  if (typeof value === "object") {
    const preferredKeys = [
      "url",
      "link",
      "redirectUrl",
      "redirect_url",
      "downloadUrl",
      "download_url",
      "boletoUrl",
      "boleto_url",
      "paymentUrl",
      "payment_url"
    ];

    for (const key of preferredKeys) {
      if (typeof value[key] === "string" && isHttpUrl(value[key])) {
        return value[key];
      }
    }

    for (const nestedKey of Object.keys(value)) {
      const found = findFirstUrlInJson(value[nestedKey]);
      if (found) {
        return found;
      }
    }
  }

  return "";
};

const getActionResultText = (action, executionData) => {
  const templateOutput = String(executionData?.resultOutput || "").trim();
  if (templateOutput) {
    return templateOutput;
  }

  const bodyPreview = String(executionData?.response?.bodyPreview || "").trim();
  if (bodyPreview) {
    return bodyPreview;
  }

  const finalUrl = String(executionData?.response?.finalUrl || "").trim();
  if (finalUrl) {
    return finalUrl;
  }

  return `A acao "${String(action?.name || "API Tools")}" foi executada com sucesso.`;
};

const getActionResultUrl = (action, executionData) => {
  const templateOutput = String(executionData?.resultOutput || "").trim();
  if (templateOutput) {
    if (isHttpUrl(templateOutput)) {
      return templateOutput;
    }

    const fromTemplate = getFirstHttpUrlFromText(templateOutput);
    if (fromTemplate) {
      return fromTemplate;
    }
  }

  const bodyPreview = String(executionData?.response?.bodyPreview || "").trim();
  if (bodyPreview) {
    try {
      const parsed = JSON.parse(bodyPreview);
      const fromJson = findFirstUrlInJson(parsed);
      if (fromJson) {
        return fromJson;
      }
    } catch (error) {
      const fromText = getFirstHttpUrlFromText(bodyPreview);
      if (fromText) {
        return fromText;
      }
    }
  }

  const finalUrl = String(executionData?.response?.finalUrl || "").trim();
  if (isHttpUrl(finalUrl)) {
    return finalUrl;
  }

  const endpoint = String(action?.endpoint || "").trim();
  if (isHttpUrl(endpoint) && !endpoint.includes("{{")) {
    return endpoint;
  }

  return "";
};

const getActionModeLabel = action => {
  const mode = normalizeResultMode(action?.resultMode);
  if (mode === "send_conversation") {
    return " - Envia na conversa";
  }
  if (mode === "open_redirect") {
    return " - Abre link";
  }
  return "";
};

const closeWindowSafely = openedWindow => {
  if (!openedWindow) {
    return;
  }

  try {
    if (!openedWindow.closed) {
      openedWindow.close();
    }
  } catch (error) {
    // noop
  }
};

const openRedirectPlaceholderTab = action => {
  if (normalizeResultMode(action?.resultMode) !== "open_redirect") {
    return null;
  }

  const openedWindow = window.open("about:blank", "_blank");
  if (!openedWindow) {
    toast.warn("Nao foi possivel abrir nova aba. Habilite pop-ups para este site.");
    return null;
  }

  try {
    openedWindow.opener = null;
    openedWindow.document.title = "Carregando...";
  } catch (error) {
    // noop
  }

  return openedWindow;
};

const ContactDrawer = ({
  open,
  handleDrawerClose,
  contact,
  loading,
  currentTicketId,
  ticketHistory = []
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [modalOpen, setModalOpen] = useState(false);
  const [localContact, setLocalContact] = useState(normalizeContact(contact));
  const [activeInfoTab, setActiveInfoTab] = useState("data");
  const [operationActions, setOperationActions] = useState([]);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [executingActionId, setExecutingActionId] = useState(null);
  const [operationResult, setOperationResult] = useState(null);
  const [historyFilter, setHistoryFilter] = useState("");
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [documentSaving, setDocumentSaving] = useState(false);
  const [documentError, setDocumentError] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [documentForm, setDocumentForm] = useState({
    personType: "",
    document: ""
  });

  const safeContact = useMemo(() => normalizeContact(localContact), [localContact]);

  const orderedHistory = useMemo(() => {
    return Array.isArray(ticketHistory) ? ticketHistory : [];
  }, [ticketHistory]);

  const filteredHistory = useMemo(() => {
    const query = String(historyFilter || "").trim().toLowerCase();
    if (!query) return orderedHistory;

    return orderedHistory.filter(item => {
      const ticketNumber = formatTicketNumber(item.id).toLowerCase();
      const subject = String(item.subject || "").toLowerCase();
      const resolutionSummary = String(item.resolutionSummary || "").toLowerCase();
      const message = String(item.lastMessage || "").toLowerCase();
      const userName = String(item.user?.name || "").toLowerCase();
      const queueName = String(item.queue?.name || "").toLowerCase();
      const status = String(formatTicketStatus(item.status) || "").toLowerCase();

      return (
        ticketNumber.includes(query) ||
        subject.includes(query) ||
        resolutionSummary.includes(query) ||
        message.includes(query) ||
        userName.includes(query) ||
        queueName.includes(query) ||
        status.includes(query)
      );
    });
  }, [historyFilter, orderedHistory]);

  useEffect(() => {
    const normalized = normalizeContact(contact);
    setLocalContact(normalized);
    setActiveInfoTab("data");
    setHistoryFilter("");
    setDocumentDialogOpen(false);
    setDocumentError("");
    setPendingAction(null);
    setDocumentForm({
      personType: normalizePersonType(normalized?.personType),
      document: formatDocument(normalized?.document || "")
    });
  }, [contact]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const loadActions = async () => {
      setLoadingOperations(true);
      try {
        const { data } = await api.get("/api-tools/actions/available");
        setOperationActions(Array.isArray(data) ? data : []);
      } catch (error) {
        setOperationActions([]);
        toastError(error);
      } finally {
        setLoadingOperations(false);
      }
    };

    loadActions();
  }, [open]);

  const executeActionRequest = async (action, contactId, redirectWindow = null) => {
    setExecutingActionId(action.id);
    setOperationResult(null);
    try {
      const { data } = await api.post(`/api-tools/actions/${action.id}/execute`, {
        ticketId: Number(currentTicketId) || undefined,
        contactId: Number(contactId) || Number(safeContact?.id) || undefined
      });

      const resultMode = normalizeResultMode(action?.resultMode);
      const requestSucceeded = Boolean(data?.success);
      const resultText = getActionResultText(action, data);
      setOperationResult({
        actionName: action.name,
        success: requestSucceeded,
        statusCode: data?.response?.statusCode,
        bodyPreview: resultText || data?.response?.bodyPreview || ""
      });

      if (!requestSucceeded) {
        if (resultMode === "open_redirect") {
          closeWindowSafely(redirectWindow);
        }
        return;
      }

      if (resultMode === "send_conversation") {
        const ticketId = Number(currentTicketId);
        if (!ticketId) {
          toast.warn("Nao foi possivel enviar o resultado: ticket nao identificado.");
          return;
        }

        const messageBody = getActionResultText(action, data);
        if (!String(messageBody || "").trim()) {
          toast.warn("A acao foi executada, mas nao retornou conteudo para enviar.");
          return;
        }

        await api.post(`/messages/${ticketId}`, {
          body: messageBody
        });
        toast.success("Resultado enviado na conversa.");
        return;
      }

      if (resultMode === "open_redirect") {
        const targetUrl = getActionResultUrl(action, data);
        if (!targetUrl) {
          closeWindowSafely(redirectWindow);
          toast.warn("A acao foi executada, mas nenhuma URL valida foi encontrada.");
          return;
        }

        if (redirectWindow && !redirectWindow.closed) {
          try {
            redirectWindow.location.replace(targetUrl);
          } catch (error) {
            try {
              redirectWindow.location.href = targetUrl;
            } catch (nestedError) {
              closeWindowSafely(redirectWindow);
              toast.warn("Nao foi possivel redirecionar a nova aba.");
              return;
            }
          }
          toast.success("Abrindo resultado da acao.");
          return;
        }

        const openedWindow = window.open(targetUrl, "_blank");
        if (!openedWindow) {
          toast.warn("Nao foi possivel abrir nova aba. Habilite pop-ups para este site.");
          return;
        }

        try {
          openedWindow.opener = null;
        } catch (error) {
          // noop
        }
        toast.success("Abrindo resultado da acao.");
      }
    } catch (error) {
      const backendErrorCode = String(
        error?.response?.data?.error || error?.response?.data?.message || ""
      ).trim();

      if (
        backendErrorCode === "ERR_API_TOOL_DOCUMENT_REQUIRED" ||
        backendErrorCode === "ERR_API_TOOL_DOCUMENT_INVALID"
      ) {
        openDocumentDialog(action);
        return;
      }

      if (normalizeResultMode(action?.resultMode) === "open_redirect") {
        closeWindowSafely(redirectWindow);
      }
      setOperationResult({
        actionName: action.name,
        success: false,
        statusCode: error?.response?.status || 0,
        bodyPreview:
          error?.response?.data?.message ||
          error?.message ||
          "Falha ao executar a acao."
      });
      toastError(error);
    } finally {
      setExecutingActionId(null);
    }
  };

  const openDocumentDialog = action => {
    setPendingAction(action);
    setDocumentError("");
    setDocumentForm({
      personType: normalizePersonType(safeContact?.personType),
      document: formatDocument(safeContact?.document || "")
    });
    setDocumentDialogOpen(true);
  };

  const handleExecuteAction = async action => {
    if (Boolean(action?.requiresDocument)) {
      if (Boolean(localContact?.isGroup)) {
        setOperationResult({
          actionName: action.name,
          success: false,
          statusCode: 400,
          bodyPreview: "Esta acao exige CPF/CNPJ e nao pode ser usada em contatos de grupo."
        });
        return;
      }

      const validation = validateDocumentAndType(
        safeContact?.document,
        safeContact?.personType
      );

      if (!validation.valid) {
        openDocumentDialog(action);
        return;
      }
    }

    const redirectWindow = openRedirectPlaceholderTab(action);
    await executeActionRequest(
      action,
      Number(safeContact?.id) || undefined,
      redirectWindow
    );
  };

  const handleSaveDocumentAndExecute = async () => {
    if (!pendingAction) {
      setDocumentDialogOpen(false);
      return;
    }

    const validation = validateDocumentAndType(
      documentForm.document,
      documentForm.personType
    );

    if (!validation.valid) {
      setDocumentError(validation.message);
      return;
    }

    if (!safeContact?.id) {
      setDocumentError("Contato invalido para atualizar CPF/CNPJ.");
      return;
    }

    setDocumentSaving(true);
    setDocumentError("");
    try {
      const { data } = await api.put(`/contacts/${safeContact.id}`, {
        personType: validation.personType,
        document: validation.document
      });

      setLocalContact(normalizeContact(data));
      setDocumentDialogOpen(false);
      const actionToExecute = pendingAction;
      setPendingAction(null);
      const redirectWindow = openRedirectPlaceholderTab(actionToExecute);
      await executeActionRequest(
        actionToExecute,
        Number(data?.id) || Number(safeContact.id),
        redirectWindow
      );
    } catch (error) {
      toastError(error);
      setDocumentError(
        error?.response?.data?.message || "Falha ao salvar CPF/CNPJ do contato."
      );
    } finally {
      setDocumentSaving(false);
    }
  };

  const handleOpenHistoryTicket = ticketId => {
    if (!ticketId) return;
    history.push(`/tickets/${ticketId}`);
    handleDrawerClose();
  };

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="right"
      open={open}
      PaperProps={{ style: { position: "absolute" } }}
      BackdropProps={{ style: { position: "absolute" } }}
      ModalProps={{
        container: document.getElementById("drawer-container"),
        style: { position: "absolute" }
      }}
      classes={{
        paper: classes.drawerPaper
      }}
    >
      <div className={classes.header}>
        <IconButton onClick={handleDrawerClose}>
          <CloseIcon />
        </IconButton>
        <Typography style={{ justifySelf: "center" }}>
          {i18n.t("contactDrawer.header", {
            defaultValue: "Dados do contato"
          })}
        </Typography>
      </div>

      {loading ? (
        <ContactDrawerSkeleton classes={classes} />
      ) : (
        <div className={classes.content}>
          <Paper square variant="outlined" className={classes.contactHeader}>
            <Avatar
              alt={safeContact.name}
              src={safeContact.profilePicUrl}
              className={classes.contactAvatar}
            />

            <Typography>{safeContact.name}</Typography>
            {safeContact.isGroup ? (
              <Chip size="small" color="primary" label="Grupo do WhatsApp" />
            ) : (
              <Typography>
                <Link href={`tel:${safeContact.number}`}>{safeContact.number}</Link>
              </Typography>
            )}
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setModalOpen(true)}
            >
              {i18n.t("contactDrawer.buttons.edit", {
                defaultValue: "Editar contato"
              })}
            </Button>
          </Paper>

          <Paper square variant="outlined" className={classes.contactDetails}>
            <ContactModal
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              contactId={safeContact.id}
              onSave={updatedContact => setLocalContact(normalizeContact(updatedContact))}
            />

            <Tabs
              value={activeInfoTab}
              onChange={(_, value) => setActiveInfoTab(value)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              className={classes.infoTabs}
            >
              <Tab label="Dados" value="data" className={classes.infoTab} />
              <Tab label="Operacao" value="operation" className={classes.infoTab} />
              <Tab label="Historico" value="history" className={classes.infoTab} />
            </Tabs>

            {activeInfoTab === "data" ? (
              <>
                <Paper square variant="outlined" className={classes.contactExtraInfo}>
                  <InputLabel>
                    {i18n.t("contactDrawer.personType", {
                      defaultValue: "Tipo de cadastro"
                    })}
                  </InputLabel>
                  <Typography component="div" style={{ paddingTop: 2 }}>
                    {safeContact?.personType || "-"}
                  </Typography>
                </Paper>
                <Paper square variant="outlined" className={classes.contactExtraInfo}>
                  <InputLabel>
                    {i18n.t("contactDrawer.document", {
                      defaultValue: "CPF/CNPJ"
                    })}
                  </InputLabel>
                  <Typography component="div" style={{ paddingTop: 2 }}>
                    {formatDocumentDisplay(safeContact?.document)}
                  </Typography>
                </Paper>
                <Typography variant="subtitle1">
                  {i18n.t("contactDrawer.extraInfo", {
                    defaultValue: "Outras informacoes"
                  })}
                </Typography>
                {(Array.isArray(safeContact?.extraInfo) ? safeContact.extraInfo : []).map(info => (
                  <Paper
                    key={info.id}
                    square
                    variant="outlined"
                    className={classes.contactExtraInfo}
                  >
                    <InputLabel>{info.name}</InputLabel>
                    <Typography component="div" noWrap style={{ paddingTop: 2 }}>
                      <MarkdownWrapper>{info.value}</MarkdownWrapper>
                    </Typography>
                  </Paper>
                ))}
              </>
            ) : activeInfoTab === "operation" ? (
              <>
                {loadingOperations ? (
                  <Box display="flex" alignItems="center" gridGap={8}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="textSecondary">
                      Carregando acoes...
                    </Typography>
                  </Box>
                ) : operationActions.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    Sem acoes disponiveis para o seu usuario.
                  </Typography>
                ) : (
                  <div className={classes.operationButtons}>
                    {operationActions.map(action => (
                      <Button
                        key={action.id}
                        variant="outlined"
                        color="primary"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handleExecuteAction(action)}
                        disabled={Number(executingActionId) === Number(action.id)}
                      >
                        {Number(executingActionId) === Number(action.id)
                          ? "Executando..."
                          : `${action.name}${action.requiresDocument ? " (CPF/CNPJ)" : ""}${getActionModeLabel(action)}`}
                      </Button>
                    ))}
                  </div>
                )}
                {operationResult ? (
                  <div className={classes.operationResult}>
                    <Typography variant="body2">
                      {operationResult.actionName} -{" "}
                      {operationResult.success ? "Sucesso" : "Falha"} (
                      {operationResult.statusCode || "-"})
                    </Typography>
                    {operationResult.bodyPreview ? (
                      <Typography className={classes.operationResultCode}>
                        {String(operationResult.bodyPreview)}
                      </Typography>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : (
              <div className={classes.historySection}>
                <Typography variant="subtitle1">Historico de tickets</Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={historyFilter}
                  onChange={event => setHistoryFilter(event.target.value)}
                  placeholder="Filtrar historico por assunto, numero, fila, atendente..."
                />
                {filteredHistory.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    {orderedHistory.length === 0
                      ? "Nenhum outro ticket encontrado para este contato."
                      : "Nenhum ticket encontrado para o filtro informado."}
                  </Typography>
                ) : (
                  filteredHistory.map(item => {
                    const isCurrent = Number(item.id) === Number(currentTicketId);
                    return (
                      <Paper
                        key={item.id}
                        variant="outlined"
                        className={`${classes.historyItem} ${
                          isCurrent ? classes.historyItemActive : ""
                        }`}
                        onClick={() => handleOpenHistoryTicket(item.id)}
                      >
                        <div className={classes.historyMeta}>
                          <div className={classes.historyBadges}>
                            <Chip size="small" label={formatTicketNumber(item.id)} />
                            <Chip
                              size="small"
                              className={classes.historyStatusChip}
                              color={item.status === "closed" ? "default" : "primary"}
                              label={formatTicketStatus(item.status)}
                            />
                            {item.isGroup && (
                              <Chip size="small" color="secondary" label="Grupo" />
                            )}
                          </div>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(item.updatedAt || item.createdAt).toLocaleString("pt-BR")}
                          </Typography>
                        </div>
                        <Box mb={0.5}>
                          <Typography variant="body2">
                            {item.user?.name ? `Atendido por ${item.user.name}` : "Sem atendente"}
                          </Typography>
                        </Box>
                        {item.subject && (
                          <Box mb={0.5}>
                            <Typography variant="body2">
                              Assunto: {item.subject}
                            </Typography>
                          </Box>
                        )}
                        {item.queue?.name && (
                          <Box mb={0.5}>
                            <Typography variant="caption" color="textSecondary">
                              Fila: {item.queue.name}
                            </Typography>
                          </Box>
                        )}
                        <Typography className={classes.historyMessage}>
                          {item.lastMessage || "Sem ultima mensagem registrada."}
                        </Typography>
                      </Paper>
                    );
                  })
                )}
              </div>
            )}
          </Paper>
        </div>
      )}

      <Dialog
        open={documentDialogOpen}
        onClose={() => {
          if (documentSaving) {
            return;
          }
          setDocumentDialogOpen(false);
          setDocumentError("");
          setPendingAction(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>CPF/CNPJ obrigatorio</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary">
            Esta acao requer CPF/CNPJ valido. O documento sera salvo no cadastro do contato antes da execucao.
          </Typography>

          <TextField
            select
            fullWidth
            margin="dense"
            variant="outlined"
            label="Tipo de cadastro"
            value={documentForm.personType}
            onChange={event => {
              const nextValue = normalizePersonType(event.target.value);
              setDocumentForm(prev => ({
                ...prev,
                personType: nextValue
              }));
            }}
            disabled={documentSaving}
          >
            <MenuItem value="">Definir automaticamente</MenuItem>
            <MenuItem value="PF">PF</MenuItem>
            <MenuItem value="PJ">PJ</MenuItem>
          </TextField>

          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label="CPF/CNPJ"
            placeholder="000.000.000-00"
            value={documentForm.document}
            onChange={event => {
              const nextValue = formatDocument(event.target.value);
              setDocumentForm(prev => ({
                ...prev,
                document: nextValue
              }));
            }}
            disabled={documentSaving}
            error={Boolean(documentError)}
            helperText={documentError || "Informe CPF/CNPJ valido para continuar."}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDocumentDialogOpen(false);
              setDocumentError("");
              setPendingAction(null);
            }}
            disabled={documentSaving}
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveDocumentAndExecute}
            color="primary"
            variant="contained"
            disabled={documentSaving}
          >
            {documentSaving ? "Salvando..." : "Salvar e executar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default ContactDrawer;
